import Vue from 'vue';
import Tooltip from 'tooltip.js';
import { mapGetters } from 'vuex';
import SavedQueries from './SavedQueries.vue';
import Utils from 'src/utils/general';
import FormatUtils from 'src/utils/formatters';
import { debounce, isEmpty } from 'lodash';
import log from 'loglevel';
import escapeRegExp from 'escape-string-regexp';
let logger = log.getLogger('QuerySelector');
export default Vue.extend({
    components: { SavedQueries },
    props: {
        hasQuery: { type: Boolean, default: false },
        compare: { type: Boolean, default: false }
    },
    data() {
        return {
            focused: false,
            state: null,
            selectedField: null,
            searchText: '',
            searchSuggestions: {
                fields: {
                    shortlist: [],
                    remaining: 0,
                },
                concepts: {
                    shortlist: [],
                    remaining: 0,
                },
                queries: {
                    shortlist: [],
                    remaining: 0,
                }
            },
            operators: [
                { "id": 1, "name": "Word Count" }
            ],
            field_lookups: {},
            searchSuggestionsDebounced: debounce(function () {
                if (isEmpty(this.field_lookups)) {
                    // `field_lookups` hasn't yet been populated. Do nothing.
                    return;
                }
                this.searchSuggestions = this.calculateSearchSuggestions(this.field_lookups);
            }, 250),
            sentimentSegments: ['mixed', 'negative', 'neutral', 'positive']
        };
    },
    computed: {
        ...mapGetters([
            'currentModel', 'sortedSegmentsForFieldsUnlimited', 'sortedFieldsUnlimited', 'savedQueries',
            'featureFlags'
        ]),
        hasSuggestions() {
            return this.searchSuggestions.fields.shortlist.length +
                this.searchSuggestions.concepts.shortlist.length +
                this.searchSuggestions.queries.shortlist.length > 0;
        },
        hasStructured() {
            return this.allFields.length > 0;
        },
        // Alphabetically sorted view of concepts
        sortedConcepts() {
            return this.currentModel.hierarchy_concepts.slice(0).sort((c1, c2) => c1.localeCompare(c2));
        },
        // Alphabetically sorted view of all fields
        allFields() {
            return this.sortedFieldsUnlimited.concat(this.currentModel.dateFields || []).sort();
        },
        // Sanitised version of `searchText`
        searchTextClean() {
            return Utils.sanitisePhraseQuery(this.searchText);
        },
        // Returns label indicating if current values of `searchText` is a word or phrase.
        wordOrPhrase() {
            return this.searchTextClean.split(' ').length > 1 ? 'phrase' : 'word';
        },
        placeholderText() {
            if (this.focused) {
                return 'Start typing or select from below...';
            }
            return this.hasQuery ? 'Add to your query' : 'Click here to begin your query';
        }
    },
    created() {
        this.state = this.hasStructured ? 'fields' : 'concepts';
        // Construct a data structure for search suggestions
        //
        // When the user types into the search box, we offer a short list
        // of suggestions for items to add to the query. The data structure
        // built below aims to make it efficient to come up with that
        // shortlist of suggestions. Most of the time, the number of unique
        // segments per field is low. But sometimes in some large datasets
        // we can have several hundred thousand unique segment values per
        // field, and in this scenario it becomes costly to filter. Thus,
        // we build the data structure below to make those lookups a bit
        // faster.
        //
        // The final data structure created here (assigned later to
        // `this.field_lookups`) is a mapping of FIELD_NAME: SET OF BIGRAMS.
        let t0 = Date.now();
        const result = {};
        let count = 0;
        for (const f of this.sortedFieldsUnlimited) {
            let segments;
            if (f.name === 'sentiment') {
                segments = this.sentimentSegments;
            }
            else {
                segments = this.sortedSegmentsForFieldsUnlimited[f.name];
            }
            let bigramsInThisField = new Set();
            const max_segments_per_field = 50000;
            let max_bigrams = 20;
            // Fields with high cardinality will be restricted to searching for
            // substrings only within the first few bigrams of each segment
            // value. This is an optimization to reduce the time cost of
            // building this data structure.
            if (segments.length > 1000)
                max_bigrams = 5;
            for (const [idx, s] of segments.entries()) {
                count += 1;
                if (idx >= max_segments_per_field)
                    break;
                // The segment value `s` is broken up into a sequence of bigrams.
                // For example, the segment value "happy" will be turned into the
                // sequence of bigrams ['ha', 'ap', 'pp', 'py'].
                for (const bg of this.bigramSequence(s, max_bigrams)) {
                    bigramsInThisField.add(bg);
                }
            }
            // Each field has its own lookup Set of bigrams. Later, given a
            // searchText, we can break up the search text into bigrams and
            // efficiently check whether every bigram given in the search text
            // appears in the lookup set. If yes, we will proceed to search
            // the sequence of segment values in that field for actual hits.
            // If no, we will skip that field entirely. Check the code for
            // calculateSearchSuggestions().
            result[f.name] = bigramsInThisField;
        }
        this.field_lookups = result;
        logger.debug(`create datastructure took ${Date.now() - t0} ms for ${count} segments`);
    },
    mounted() {
        this.$nextTick(() => {
            if (!this.hasStructured) {
                new Tooltip(this.$el.querySelector('.no-fields'), {
                    title: '<p>No structured data fields found in this Analysis.</p>',
                    html: true
                });
            }
        });
        // Setup click handler on document in order to hide popover.
        document.addEventListener('click', this.onDocumentClick.bind(this));
    },
    beforeDestroy: function () {
        // Cleanup click handler for popover behaviour
        document.removeEventListener('click', this.onDocumentClick);
    },
    methods: {
        number: FormatUtils.number,
        // Compute the left offset for the popover -- to make sure we don't overflow screen right. The offset will be negative.
        popoverLeft() {
            if (this.compare && this.$el) {
                let padding = -20;
                let width = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
                let popover = this.$el.querySelector('.query-popover');
                let rect = popover ? popover.getBoundingClientRect() : null;
                // If the right side of the popover overflows the screen, adjust the left position
                if (rect && rect.right > width) {
                    return width - rect.right + padding;
                }
                return 0;
            }
        },
        // Bubble up query deleted event
        bubbleQueryDeleted(queryId, updatedQueries) {
            this.$emit('query-deleted', queryId, updatedQueries);
        },
        // Bubble up query renamed event
        bubbleQueryRenamed(query) {
            this.$emit('query-renamed', query);
        },
        // Bubble up query saved event
        bubbleQuerySaved(query) {
            this.$emit('query-saved', query);
        },
        // Bubble up query selected event
        bubbleQuerySelected(query) {
            this.$emit('query-selected', query);
        },
        // When clicking the broader search input box,
        // set `focused` flag and shift UI focus to the actual search input.
        focusSearch() {
            this.focused = true;
            this.$refs.searchInput.focus();
            this.preventBlur = true;
        },
        // Hide and reset the query selector
        hide() {
            this.focused = false;
            this.selectedField = null;
            this.searchText = '';
        },
        // Infer concept from variant
        inferVariant(value) {
            if (this.currentModel.variantsMap[value]) {
                return this.currentModel.variantsMap[value].name;
            }
            return value;
        },
        // Search based on `searchText` value.
        doManualSearch(textValue) {
            if (this.searchTextClean.length > 0) {
                let value = this.inferVariant(this.searchTextClean);
                this.$emit('text-added', value);
            }
            this.hide();
        },
        // Hide/reset popover when the document background is clicked.
        onDocumentClick() {
            if (this.preventBlur) {
                this.preventBlur = false;
                return;
            }
            this.hide();
        },
        // Handle change in search input by focusing if applicable
        onInput() {
            if (!this.focused && this.$refs.searchInput.value.length > 0) {
                this.focusSearch();
            }
            this.searchSuggestionsDebounced();
        },
        // Add concept to query
        selectConcept(conceptName) {
            this.$emit('text-added', conceptName);
            this.hide();
        },
        // Clicking on a field..
        selectField(field, event) {
            this.selectedField = field;
            this.$emit('field-added', field.name);
            this.hide();
        },
        // Clicking on an operator..
        selectOperator(operator) {
            this.selectedOperator = operator;
            this.$emit('operator-added', operator);
            this.hide();
        },
        selectQuery(query) {
            this.$emit('query-added', query);
            this.hide();
        },
        // Add segment to query based on search suggestions.
        selectSegmentWithField(field, segment) {
            if (segment === undefined) {
                // Date fields handled specially (no segment)
                this.$emit('field-added', field);
            }
            else {
                this.$emit('segment-added', field, segment);
            }
            this.hide();
        },
        /**
         * Produce a sequence of character bigrams from a string.
         *
         * Example:
         *
         *    "happy" -> ["ha", "ap", "pp", "py"]
         *
         * Note that there is a bailout parameter to be able to control the
         * number of bigrams produced by very long strings. In the case that
         * the max limit is reached, the bigram will represent the start of
         * the string, up to the limit.
         *
         * @param {string} str
         * @param {number} max_bigrams
         * @returns {Array<string>}
         */
        bigramSequence(str, max_bigrams = 10) {
            let s = str.toLowerCase();
            const result = [];
            for (let i = 0; i < s.length - 1; i++) {
                result.push(s.slice(i, i + 2));
                if (i >= max_bigrams)
                    break;
            }
            return result;
        },
        /**
         * Returns search suggestions (fields & concepts) based on current
         * `searchText` value.
         * @param {Object<string, Set<string>>} field_lookups
         * @returns {{concepts: {shortlist: *, remaining: number}, fields: {shortlist: [], remaining: number}}}
         */
        calculateSearchSuggestions(field_lookups) {
            let t0 = Date.now();
            let searchText = new RegExp(escapeRegExp(this.searchText.trim()), 'i');
            let potentialFields = [];
            // We want to do an early bail out when matching the first 10 of each
            // group of data. That means we have to keep track of how many matches
            // we have made so far.
            let matches = 0;
            const maxMatches = 10;
            // This is a helper function so we don't have to keep pushing and
            // updating the counter
            function addToMatches(field, segment, group) {
                const match = { field: field };
                // More special handling for dates...
                if (segment) {
                    match.segment = segment;
                }
                group.push(match);
                ++matches;
            }
            let searchTextBigrams = [...this.bigramSequence(this.searchText.trim(), 
                // The search text bigrams should always include everything.
                100)];
            for (const f of this.sortedFieldsUnlimited) {
                if (matches >= maxMatches) {
                    break;
                }
                // This block does optimized bigram-matching to speed up search
                // to offer suggestions for queries based on text the user types
                // in. The work is focused on segment values, and is described
                // in more detail in the comments below. However, we just need to
                // point out here that IF the field name itself matches the
                // search text then we don't need to do any optimized bigram-
                // matching stuff, since we already know we're going to get some
                // hits based on the field name alone.
                if (!f.name.match(searchText)) {
                    // Make use of our custom data structure. We break up the search
                    // text into bigrams, and then we verify that this field contains
                    // every single bigram in the search text. If it doesn't, we don't
                    // bother searching the searchText against the actual segment
                    // values at all.
                    let lookup = field_lookups[f.name];
                    let thisFieldContainsAllSearchTextBigrams = true;
                    for (const bigram of searchTextBigrams) {
                        if (!lookup.has(bigram)) {
                            thisFieldContainsAllSearchTextBigrams = false;
                            break;
                        }
                    }
                    if (!thisFieldContainsAllSearchTextBigrams) {
                        // There's no point searching the segments in this field, since we
                        // know it lacks one or more bigrams in the search text. Just
                        // skip this field and move onto the next one.
                        continue;
                    }
                }
                // Unfortunately sortedFields treats sentiment as a special case and
                // does not include the segment values, so we have to handle that
                let segments;
                if (f.name === 'sentiment') {
                    segments = this.sentimentSegments;
                }
                else {
                    segments = this.sortedSegmentsForFieldsUnlimited[f.name];
                }
                for (const s of segments) {
                    if (matches >= maxMatches) {
                        break;
                    }
                    // We have to check the field name while looping over the segments,
                    // because we need both the field name and the segments values
                    // when adding to the list of potential matches
                    if (f.name.match(searchText)) {
                        addToMatches(f.name, s, potentialFields);
                        continue;
                    }
                    if (s.match(searchText)) {
                        addToMatches(f.name, s, potentialFields);
                    }
                }
            }
            if (this.currentModel.dateFields) {
                for (const f of this.currentModel.dateFields) {
                    if (matches >= maxMatches) {
                        break;
                    }
                    if (f.name.match(searchText)) {
                        addToMatches(f.name, null, potentialFields);
                    }
                }
            }
            const nFieldsLeft = (Object.values(this.sortedSegmentsForFieldsUnlimited).reduce((a, s) => a + s.length, 0)
                + this.currentModel.dateFields.length
                - 10);
            let suggestedConcepts = this.sortedConcepts.filter((c) => c.match(searchText));
            let suggestedQueries = this.savedQueries.filter((c) => c.name.match(searchText));
            logger.debug(`search suggestions took ${Date.now() - t0} ms`);
            return {
                fields: {
                    shortlist: potentialFields,
                    remaining: Math.max(nFieldsLeft, 0)
                },
                concepts: {
                    shortlist: suggestedConcepts.slice(0, 10),
                    remaining: Math.max(suggestedConcepts.length - 10, 0)
                },
                queries: {
                    shortlist: suggestedQueries.slice(0, 10),
                    remaining: Math.max(suggestedQueries.length - 10, 0)
                }
            };
        },
    }
});
